/**
 * Canonical tenant identity for observability (W0.3 · F14 / G14).
 *
 * **Definition:** the user's `user_tenants` row with `isDefault = true`, else
 * any membership. That is what `AuthGuard` resolves for every request, what
 * tenant-isolated queries filter on, and it is DB-enforced (partial unique
 * index on one default row per user). Frontends read the same rows from
 * `GET /api/auth/me/portals`, whose query INNER JOINs `tenants` — so any id it
 * returns provably exists in `tenants`.
 *
 * The **sponsoring company is a different relationship** and travels as a
 * separate `sponsorTenantId`: an invited citizen's journey lives in the
 * CITIZEN's tenant, not the sponsor's, so a sponsor id must never overwrite
 * this one.
 *
 * A missing tag is honest; a wrong tag is not — this returns `undefined`
 * rather than guessing, and rejects anything that is not shaped like a tenant
 * UUID (a Clerk `user_…` id, an internal user UUID copied by mistake, an empty
 * string). Tagging events with a user id is the exact defect being fixed.
 */

/** Shape of one `user_tenants` membership as the portals endpoint returns it. */
export interface TenantMembershipLike {
  tenantId: string;
  isDefault?: boolean | null;
}

/**
 * 8-4-4-4-12 hex. Deliberately NOT an RFC-4122 version/variant check: seeded
 * E2E fixture ids carry version nibble `0` and are real rows in `tenants`.
 */
const TENANT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `value` is shaped like a tenant UUID (never a Clerk `user_…` id). */
export function isTenantId(value: unknown): value is string {
  return typeof value === 'string' && TENANT_UUID.test(value.trim());
}

/**
 * Resolve the observability tenant id from a user's memberships.
 * Returns `undefined` when there is nothing trustworthy to report.
 */
export function resolveTenantId(
  memberships: readonly TenantMembershipLike[] | null | undefined
): string | undefined {
  if (!memberships || memberships.length === 0) return undefined;

  const usable = memberships.filter((m) => isTenantId(m?.tenantId));
  if (usable.length === 0) return undefined;

  const preferred = usable.find((m) => m.isDefault === true) ?? usable[0];
  return preferred.tenantId.trim();
}
